import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import PlatformDetector from './PlatformDetector';

/**
 * Abstracción de almacenamiento que funciona en todas las plataformas
 * Usa localStorage en web y AsyncStorage en plataformas nativas
 */
const Storage = {
  /**
   * Obtiene un valor del almacenamiento
   * @param {string} key - Clave para obtener
   * @returns {Promise<string|null>} - Valor almacenado o null si no existe
   */
  getItem: async (key) => {
    try {
      if (PlatformDetector.isWeb()) {
        // En web, verificamos si localStorage está disponible (no lo estará durante SSR)
        if (typeof localStorage !== 'undefined') {
          return localStorage.getItem(key);
        }
        return null;
      } else {
        // En plataformas nativas, usamos AsyncStorage
        return await AsyncStorage.getItem(key);
      }
    } catch (error) {
      console.error(`Error al obtener ${key} del almacenamiento:`, error);
      return null;
    }
  },

  /**
   * Almacena un valor
   * @param {string} key - Clave para almacenar
   * @param {string} value - Valor para almacenar
   * @returns {Promise<void>}
   */
  setItem: async (key, value) => {
    try {
      if (PlatformDetector.isWeb()) {
        // En web, verificamos si localStorage está disponible
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(key, value);
        }
      } else {
        // En plataformas nativas, usamos AsyncStorage
        await AsyncStorage.setItem(key, value);
      }
    } catch (error) {
      console.error(`Error al almacenar ${key} en el almacenamiento:`, error);
    }
  },

  /**
   * Elimina un valor del almacenamiento
   * @param {string} key - Clave a eliminar
   * @returns {Promise<void>}
   */
  removeItem: async (key) => {
    try {
      if (PlatformDetector.isWeb()) {
        // En web, verificamos si localStorage está disponible
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(key);
        }
      } else {
        // En plataformas nativas, usamos AsyncStorage
        await AsyncStorage.removeItem(key);
      }
    } catch (error) {
      console.error(`Error al eliminar ${key} del almacenamiento:`, error);
    }
  },

  /**
   * Obtiene todas las claves almacenadas
   * @returns {Promise<string[]>} - Array de claves
   */
  getAllKeys: async () => {
    try {
      if (PlatformDetector.isWeb()) {
        // En web, verificamos si localStorage está disponible
        if (typeof localStorage !== 'undefined') {
          return Object.keys(localStorage);
        }
        return [];
      } else {
        // En plataformas nativas, usamos AsyncStorage
        return await AsyncStorage.getAllKeys();
      }
    } catch (error) {
      console.error('Error al obtener todas las claves del almacenamiento:', error);
      return [];
    }
  },

  /**
   * Elimina todos los valores del almacenamiento
   * @returns {Promise<void>}
   */
  clear: async () => {
    try {
      if (PlatformDetector.isWeb()) {
        // En web, verificamos si localStorage está disponible
        if (typeof localStorage !== 'undefined') {
          localStorage.clear();
        }
      } else {
        // En plataformas nativas, usamos AsyncStorage
        await AsyncStorage.clear();
      }
    } catch (error) {
      console.error('Error al limpiar el almacenamiento:', error);
    }
  }
};

export default Storage;
